import { SyntheticEvent, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

// material-ui
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// third-party
import { useFormik } from 'formik';
import { Eye, EyeSlash } from 'iconsax-reactjs';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';

// project-imports
import AnimateButton from 'components/@extended/AnimateButton';
import IconButton from 'components/@extended/IconButton';
import Logo from 'components/logo';
import { isDemoMode } from 'config/demo-mode';
import { demoCredentials } from 'demo/mock-data';
import { useAuth } from 'features/auth';
import { getApiErrorMessage } from 'lib/api-client';

interface LocationState {
  from?: string;
}

interface LoginFormValues {
  email: string;
  password: string;
  submit?: string;
}

const validationSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup.string().required('Password is required')
});

export default function LoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik<LoginFormValues>({
    initialValues: {
      email: isDemoMode ? demoCredentials.email : '',
      password: isDemoMode ? demoCredentials.password : ''
    },
    validationSchema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      try {
        await login({
          email: values.email.trim(),
          password: values.password
        });

        enqueueSnackbar('Login berhasil.', { variant: 'success' });

        const state = location.state as LocationState | null;
        navigate(state?.from || '/dashboard', { replace: true });
      } catch (error) {
        const message = getApiErrorMessage(error);
        setErrors({ submit: message });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleClickShowPassword = () => {
    setShowPassword((current) => !current);
  };

  const handleMouseDownPassword = (event: SyntheticEvent) => {
    event.preventDefault();
  };

  return (
    <Stack sx={{ gap: { xs: 3, sm: 4 } }}>
      <Stack sx={{ gap: 3, alignItems: { xs: 'center', sm: 'flex-start' }, textAlign: { xs: 'center', sm: 'left' } }}>
        <Logo />
        <Stack sx={{ gap: 1.2 }}>
          <Typography variant="h1" sx={{ fontWeight: 800, letterSpacing: -1.2, fontSize: { xs: 32, sm: 42, lg: 52 } }}>
            Halo! Selamat Datang di MineACS 👋
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 560, fontSize: { xs: 15, sm: 16 }, lineHeight: 1.75 }}>
            Masuk untuk memantau perangkat, posisi unit, telemetry, dan safety event area tambang secara real-time.
          </Typography>
        </Stack>
      </Stack>

      <Stack component="form" noValidate onSubmit={formik.handleSubmit} sx={{ gap: 3, width: '100%' }}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Stack sx={{ gap: 1 }}>
              <InputLabel htmlFor="email-login">Email</InputLabel>
              <OutlinedInput
                fullWidth
                id="email-login"
                name="email"
                type="email"
                placeholder="Enter email address"
                autoComplete="email"
                value={formik.values.email}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                disabled={formik.isSubmitting}
                error={formik.touched.email && Boolean(formik.errors.email)}
              />
            </Stack>
            {formik.touched.email && formik.errors.email && <FormHelperText error>{formik.errors.email}</FormHelperText>}
          </Grid>

          <Grid size={12}>
            <Stack sx={{ gap: 1 }}>
              <InputLabel htmlFor="password-login">Password</InputLabel>
              <OutlinedInput
                fullWidth
                id="password-login"
                name="password"
                placeholder="Enter password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={formik.values.password}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                disabled={formik.isSubmitting}
                error={formik.touched.password && Boolean(formik.errors.password)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      edge="end"
                      color="secondary"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? <Eye /> : <EyeSlash />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Stack>
            {formik.touched.password && formik.errors.password && <FormHelperText error>{formik.errors.password}</FormHelperText>}
          </Grid>

          <Grid size={12} sx={{ mt: -1 }}>
            <Stack direction="row" sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
              <Link component={RouterLink} to="/forgot-password" variant="subtitle2" sx={{ color: '#e53935', fontWeight: 600 }}>
                Lupa Password?
              </Link>
            </Stack>
          </Grid>

          {formik.errors.submit && (
            <Grid size={12}>
              <FormHelperText error>{formik.errors.submit}</FormHelperText>
            </Grid>
          )}

          <Grid size={12}>
            <AnimateButton>
              <Button
                disableElevation
                disabled={formik.isSubmitting}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                sx={{
                  bgcolor: '#e53935',
                  py: 1.4,
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#c62828' }
                }}
              >
                {formik.isSubmitting ? 'Logging in...' : 'Login'}
              </Button>
            </AnimateButton>
          </Grid>

          <Grid size={12}>
            <Typography variant="body2" align="center" color="text.secondary">
              Belum punya akun?{' '}
              <Link component={RouterLink} to="/settings" sx={{ color: '#e53935', fontWeight: 700, textDecoration: 'none' }}>
                Hubungi Admin
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </Stack>
    </Stack>
  );
}
